import { UploadFile } from 'antd/lib/upload/interface';
import { Comment, Discussion } from 'api/document/types/document';
import { HttpClient } from 'api/http-client';
import { User } from 'api/models';
import { constructFormData } from './construct-form-data';
import { AttachmentType } from './types';

export type FileAttachment = {
  name: string;
  sizeInBytes: number;
  location: string;
  createdBy: number;
  createdAt: number;
  projectId: number;
  attachmentId: number;
  accountId: number;
  context?: AttachmentType;
  publicVisibility: boolean;
  isDeleted: string;
  relativePath: string;
  downloadUrl?: string;
  thumbLocation?: string;
  metadata?: Record<string, any>;
  sourceId?: number;
  sourceType?: AttachmentType;
  latestVersion: boolean;
  versionMappingId: string;
  isAnnotationStarted?: boolean;
  versionsAvailable?: boolean;
  commentThreadId?: number;
  isPrivate?: boolean;
  createdByObject?: User;
  isPinned: boolean;
};

export type FileAttachmentV2 = {
  attachment: FileAttachment;
  link: string;
  source?: string;
};

export interface UploadFileWithAttachment extends UploadFile {
  attachment?: FileAttachment;
}

export enum FileUploadStatus {
  Done = 'done',
  Error = 'error',
  Success = 'success',
  Uploading = 'uploading',
  Removed = 'removed',
}

export interface UpdateAttachmentComment {
  commentMeta: {
    annotations: Record<string, any>;
  };
  isPrivate: boolean;
  content: string;
  commentGuid: string;
}

type UpdateCommentRequestBody = {
  comment: Omit<UpdateAttachmentComment, 'isPrivate'> & {
    private: boolean;
  };
};

export const MAX_FILE_SIZE = 31457280; // 30 MB;

export class AttachmentsHttpClient extends HttpClient {
  async postRequest(formData: FormData): Promise<FileAttachment> {
    return (
      await this.post<FormData, FormData, { attachment: FileAttachment }>(
        '/attachments',
        formData,
      )
    ).attachment;
  }

  public async deleteAttachment(attachmentID: number) {
    return this.delete(`attachments/${attachmentID}/`);
  }

  public async attachTaskFiles(
    file: File,
    data: { projectId: number; taskId: number },
  ): Promise<FileAttachment> {
    const formData = constructFormData(file, {
      attachment: {
        projectId: data.projectId,
      },
      associatedEntity: {
        sourceType: AttachmentType.Task,
        sourceId: data.taskId,
      },
    });

    return this.postRequest(formData);
  }

  public async attachTemplateTaskFile(file: File): Promise<FileAttachment> {
    const formData = constructFormData(file, {});

    return this.postRequest(formData);
  }

  public async attachStatusUpdateFiles(
    file: File,
    data: { projectId: number; statusUpdateId: number },
  ): Promise<FileAttachment> {
    const formData = constructFormData(file, {
      attachment: {
        projectId: data.projectId,
      },
      associatedEntity: {
        sourceType: AttachmentType.StatusUpdate,
        sourceId: data.statusUpdateId,
      },
    });

    return this.postRequest(formData);
  }

  public async attachFiles(
    file: File,
    data: { projectId: number },
  ): Promise<FileAttachment> {
    const formData = constructFormData(file, {
      attachment: {
        projectId: data.projectId,
      },
    });

    return (
      await this.post<FormData, FormData, { attachment: FileAttachment }>(
        'attachments',
        formData,
      )
    ).attachment;
  }

  public deleteFile(
    accountId: number,
    projectId: number,
    attachmentId: number,
  ): Promise<void> {
    return this.delete<void>(
      `/account/${accountId}/project/${projectId}/attachment/${attachmentId}`,
    );
  }

  public async getAttachment(attachmentId: number): Promise<FileAttachment> {
    return (
      await this.get<FileAttachment, { attachment: FileAttachment }>(
        `attachments/${attachmentId}`,
      )
    ).attachment;
  }

  public getDownloadLinkForAttachmentsInTask(
    projectId: number,
    taskId: number,
  ) {
    return `${this.BASEURL}/projects/${projectId}/tasks/${taskId}/attachments/download`;
  }

  public downloadAttachmentsInTask(projectId: number, taskId: number) {
    return this.get(
      `projects/${projectId}/tasks/${taskId}/attachments/download`,
      { responseType: 'blob' },
    );
  }

  public updateAttachmentMetadata(
    attachmentId: number,
    metadata: Record<string, any> = {},
  ) {
    return this.put(`attachments/${attachmentId}`, { metadata });
  }

  public addComment(
    attachmentId: number,
    {
      commentMeta: { annotations },
      commentGuid,
      content,
      isPrivate,
    }: UpdateAttachmentComment,
  ) {
    return this.post<Comment, UpdateCommentRequestBody>(
      `/attachments/${attachmentId}/discussions`,
      {
        comment: {
          commentMeta: { annotations },
          commentGuid,
          content,
          private: isPrivate,
        },
      },
    );
  }

  public getDiscussions(
    attachmentId: number,
  ): Promise<{ discussions: Discussion[] }> {
    return this.get(`/attachments/${attachmentId}/discussions`);
  }

  public updateComment(
    attachmentId: number,
    commentId: number,
    body: Partial<UpdateAttachmentComment>,
  ): Promise<Comment> {
    return this.put(`/attachments/${attachmentId}/comments/${commentId}`, {
      comment: body,
    });
  }

  public deleteCommentThread = (
    attachmentId: number,
    commentThreadId: number,
  ) => {
    return this.delete(
      `/attachments/${attachmentId}/discussions/${commentThreadId}`,
    );
  };

  public addReply(
    attachmentId: number,
    threadId: number,
    {
      content,
      isPrivate,
    }: Pick<UpdateAttachmentComment, 'content' | 'isPrivate'>,
  ): Promise<Comment> {
    return this.post(
      `/attachments/${attachmentId}/discussions/${threadId}/comments`,
      {
        comment: { content, private: isPrivate },
      },
    );
  }

  public resolveDocumentThread(
    attachmentId: number,
    commentThreadId: number,
    resolved: boolean,
  ) {
    return this.put(
      `/attachments/${attachmentId}/discussions/${commentThreadId}`,
      { resolved },
    );
  }

  public deleteComment = (attachmentId: number, commentId: number) => {
    return this.delete(`/attachments/${attachmentId}/comments/${commentId}`);
  };

  public async createAttachmentVersion(
    parentAttachmentId: number,
    file: File,
    data: { projectId: number; sourceId?: number; sourceType?: AttachmentType },
  ): Promise<FileAttachment> {
    const formData = constructFormData(file, {
      attachment: {
        projectId: data.projectId,
      },
      associatedEntity: {
        sourceType: data.sourceType,
        sourceId: data.sourceId ?? data.projectId,
      },
    });

    return (
      await this.post<FormData, FormData, { attachment: FileAttachment }>(
        `/attachments/${parentAttachmentId}/version`,
        formData,
      )
    ).attachment;
  }

  public deleteAttachmentVersion = (attachmentVersionId: number) => {
    return this.delete(`/attachments/${attachmentVersionId}/version`);
  };

  public async getAttachmentVersion(
    attachmentVersionId: number,
  ): Promise<FileAttachment> {
    return this.get<FileAttachment>(
      `/attachments/version/${attachmentVersionId}`,
    );
  }

  public async getAllAttachmentVersions(
    attachmentId?: number,
  ): Promise<FileAttachment[]> {
    return this.get<FileAttachment[]>(`/attachments/${attachmentId}/version`);
  }

  public async markAsCurrentVersion(
    attachmentId: number,
    versionMappingId: number,
  ): Promise<void> {
    return this.put(
      `/attachments/${attachmentId}/version/${versionMappingId}/current`,
      {},
    );
  }

  public updateLastSeen(id: number): Promise<FileAttachment> {
    return this.post(`/attachments/${id}/last-seen`, {});
  }
}

export const getUpdatedFileList = (
  file: UploadFileWithAttachment,
  fileList: UploadFileWithAttachment[],
  attachment?: FileAttachment,
  status?: FileUploadStatus,
): Array<UploadFileWithAttachment> => {
  const updatedFile: UploadFileWithAttachment = {
    ...file,
    attachment,
    status,
    thumbUrl: attachment?.location,
    url: attachment?.location,
  };

  return fileList.map((f) => (f.uid === updatedFile.uid ? updatedFile : f));
};

export const AttachmentsAPI = new AttachmentsHttpClient();
